import { addHours } from "date-fns";
import { PlanBase } from "../models/PlanBase";
import { SubscriptionBase } from "../models/SubscriptionBase";
import { WithId } from "./Id";
import { ReplaceProps } from "./ReplaceProps";

export const SUBSCRIPTION_END_GRACE_PERIOD_HOURS = 48;

export type SubscriptionWithPlan = ReplaceProps<
  SubscriptionBase<any>,
  "plan",
  {
    plan: WithId<PlanBase<any>>;
  }
>;

export function hasValidPlanWith({
  cond,
  subscriptions,
}: {
  subscriptions: SubscriptionWithPlan[];
  cond: (props: { plan: PlanBase<any> }) => boolean;
}): boolean {
  return getValidSubscriptions({
    subscriptions: subscriptions,
  }).some((s) =>
    cond({
      plan: s.plan,
    }),
  );
}

export function hasValidPlanForTransactionCount({
  transactionCount,
  subscriptions,
}: {
  subscriptions: SubscriptionWithPlan[];
  transactionCount: number;
}): boolean {
  return getValidSubscriptions({
    subscriptions: subscriptions,
  }).some((subscription) => {
    return transactionCount <= subscription.plan.features.transactionLimit;
  });
}

export function getValidSubscriptions<TSub extends SubscriptionBase<any>>({
  subscriptions,
}: {
  subscriptions: TSub[];
}) {
  const now = new Date();

  return subscriptions.filter(
    (subscription) =>
      subscription.status === "valid" &&
      now >= subscription.begins &&
      now <= addHours(subscription.ends, SUBSCRIPTION_END_GRACE_PERIOD_HOURS),
  );
}

export enum PlanStatus {
  None = "None",
  Active = "Active",
  DowngradeScheduled = "Downgrade Scheduled", // will downgrade on renewal
  Cancelled = "Cancelled", // scheduled for cancellation
  RequiresAction = "Requires Action", // payment past due requiring billing details to be updated
  Expired = "Expired", // finalised state where the subscription can no longer be renewed
}

export type SubscriptionState = {
  // the plan is active and the user has access to the features.
  valid: boolean;

  downgrading: boolean;

  // the plan will end cancel at the end of the period.
  cancelled: boolean;

  // plan has not been paid up to today's date.
  lapsed: boolean;

  // the plan is in a finalised state which cannot be renewed.
  // the user must purchase a new plan to resume.
  expired: boolean;

  // the date the plan renewal will occur.
  renewsOn: Date;

  // the date the plan features will no longer be available if the payment
  // is not made, a few fays after the `renewsOn` date.
  lapsesOn: Date;

  // summarises all subscription state factors into a single status
  planStatus: PlanStatus;

  // expiresOn can be calculated if needed, it's currently two weeks after
  // `renewsOn`, or another date if manually scheduled.
};

export function getSubscriptionState(
  subscription: Pick<
    SubscriptionBase<any>,
    | "status"
    | "isCancelled"
    | "ends"
    | "stripeLastStatus"
    | "downgradeToPlan"
    | "current"
  >,
): SubscriptionState {
  const now = new Date();
  const lapsed =
    addHours(subscription.ends, SUBSCRIPTION_END_GRACE_PERIOD_HOURS) < now;
  const downgrading = !!subscription.downgradeToPlan;
  const cancelled = !!subscription.isCancelled;
  const expired =
    subscription.stripeLastStatus === "canceled" ||
    !subscription.current ||
    // check if it's a lapsed manual plan (no stripe id)
    (!subscription.stripeLastStatus && lapsed);
  const valid = subscription.status === "valid" && !lapsed;
  const lapsesOn = addHours(
    subscription.ends,
    SUBSCRIPTION_END_GRACE_PERIOD_HOURS,
  );
  const renewsOn = subscription.ends;
  const planStatus = parsePlanStatus({
    downgrading,
    cancelled,
    expired,
    valid,
  });

  return {
    lapsed,
    downgrading,
    cancelled,
    expired,
    valid,
    lapsesOn,
    renewsOn,
    planStatus,
  };
}

function parsePlanStatus({
  downgrading,
  cancelled,
  expired,
  valid,
}: {
  downgrading: boolean;
  cancelled: boolean;
  expired: boolean;
  valid: boolean;
}): PlanStatus {
  if (expired) {
    return PlanStatus.Expired;
  }

  if (!valid) {
    return PlanStatus.RequiresAction;
  }

  if (cancelled) {
    return PlanStatus.Cancelled;
  }

  if (downgrading) {
    return PlanStatus.DowngradeScheduled;
  }

  return PlanStatus.Active;
}

/**
 * Get current subscription. Stripe based first or manual otherwise.
 * @param subscriptions
 */
export const getCurrentSubscription = <
  TSub extends Pick<
    SubscriptionBase<any>,
    | "current"
    | "stripeSubscriptionId"
    | "stripeLastStatus"
    | "downgradeToPlan"
    | "ends"
    | "isCancelled"
    | "status"
  >,
>(
  subscriptions: TSub[],
): TSub | undefined => {
  return (
    // any plan which isn't expired and uses a stripe subscription is preferred.
    subscriptions.find(
      (s) =>
        getSubscriptionState(s).planStatus !== PlanStatus.Expired &&
        s.stripeSubscriptionId,
    ) ??
    // otherwise, it could be a non-expired manual plan
    subscriptions.find(
      (s) => getSubscriptionState(s).planStatus !== PlanStatus.Expired,
    ) ??
    // otherwise, it could be an expired stripe plan
    subscriptions.find((s) => s.stripeSubscriptionId) ??
    // otherwise, it could be anything left, such as an expired manual plan
    subscriptions[0]
  );
};
